/**
 * Revenue Chart Component
 * Phase 6 Batch 3 - Core 4 Reports
 *
 * Line/Bar chart showing revenue trend over time
 */
import { useRevenueByMonth } from '@/hooks/useDashboardReports';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function RevenueChart({ months = 6 }: { months?: number }) {
  const { data, isLoading, error } = useRevenueByMonth(months);
  const navigate = useNavigate();

  // MED-05: Handle bar click to navigate to invoices filtered by month
  const handleBarClick = (data: any) => {
    if (data?.activePayload?.[0]?.payload?.month) {
      const month = data.activePayload[0].payload.month;
      navigate(`/invoices?month=${month}`);
    }
  };

  // MED-05: Navigate to outstanding invoices
  const handleOutstandingClick = () => {
    navigate('/invoices?view=unpaid');
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load revenue data
      </div>
    );
  }

  // Reverse data to show oldest to newest (left to right)
  const chartData = [...(data?.data || [])].reverse();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="revenue-chart">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Revenue Trend (Last {months} Months)
      </h2>
      {/* MED-05: Added onClick handler for bar click navigation */}
      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
          onClick={handleBarClick}
          className="cursor-pointer"
        >
          <CartesianGrid strokeDasharray="0" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="month_label"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            angle={-45}
            textAnchor="end"
            height={80}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={{ stroke: '#e5e7eb' }}
          />
          <Tooltip
            formatter={(value: number) => formatCurrency(value)}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}
          />
          <Legend
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="square"
          />
          <Bar dataKey="paid" name="Paid" fill="#10b981" stackId="a" radius={[0, 0, 0, 0]} />
          <Bar dataKey="outstanding" name="Outstanding" fill="#3b82f6" stackId="a" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      {/* MED-05: Made Outstanding card clickable */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-gray-600">Total Revenue:</span>
          <span className="ml-2 font-semibold">
            {formatCurrency(chartData.reduce((sum, month) => sum + month.revenue, 0))}
          </span>
        </div>
        <div>
          <span className="text-gray-600">Total Paid:</span>
          <span className="ml-2 font-semibold text-emerald-600">
            {formatCurrency(chartData.reduce((sum, month) => sum + month.paid, 0))}
          </span>
        </div>
        <div
          className="cursor-pointer hover:bg-blue-50 rounded p-1 -m-1 transition-colors"
          onClick={handleOutstandingClick}
          data-testid="outstanding-summary-link"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleOutstandingClick()}
        >
          <span className="text-gray-600">Total Outstanding:</span>
          <span className="ml-2 font-semibold text-sky-600 underline">
            {formatCurrency(chartData.reduce((sum, month) => sum + month.outstanding, 0))}
          </span>
        </div>
      </div>
    </div>
  );
}
